import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Plus, Search, Package, PackagePlus, Trash2 } from 'lucide-react';
import api, { fmtIDR } from '@/lib/api';
import { toast } from 'sonner';
import EmptyState from '@/components/common/EmptyState';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { useAuth } from '@/lib/AuthContext';

export default function ProductsManager({ showHpp = false }) {
  const { user } = useAuth();
  const canDelete = user?.role === 'owner' || user?.role === 'admin';
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [stockInFor, setStockInFor] = useState(null);

  const load = async () => {
    const [p, s] = await Promise.all([
      api.get('/products?active_only=false'),
      api.get('/suppliers'),
    ]);
    setProducts(p.data);
    setSuppliers(s.data);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => products.filter((p) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return p.name.toLowerCase().includes(q) || p.sku.toLowerCase().includes(q) || p.category.toLowerCase().includes(q);
  }), [products, query]);

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Cari SKU / nama / kategori" value={query} onChange={(e) => setQuery(e.target.value)} className="pl-9" data-testid="admin-products-search" />
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="admin-add-product-button"><Plus className="h-4 w-4 mr-2" />Tambah Barang</Button></DialogTrigger>
          <AddProductDialog suppliers={suppliers} onSaved={() => { load(); setOpen(false); }} />
        </Dialog>
      </div>

      {filtered.length === 0 ? (
        <EmptyState icon={Package} title="Belum ada barang" description="Tambahkan barang baru untuk mulai transaksi." />
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>SKU</TableHead>
                    <TableHead>Nama</TableHead>
                    <TableHead>Kategori</TableHead>
                    <TableHead className="text-right">Harga Jual</TableHead>
                    <TableHead className="text-right">Stok</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((p) => {
                    const low = p.stock <= (p.low_stock_threshold || 5);
                    return (
                      <TableRow key={p.id} data-testid={`admin-product-row-${p.id}`}>
                        <TableCell className="font-mono text-xs">{p.sku}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            {p.photo_url ? <img src={p.photo_url} alt={p.name} className="h-10 w-10 rounded object-cover" /> : <div className="h-10 w-10 rounded bg-muted flex items-center justify-center"><Package className="h-4 w-4 text-muted-foreground" /></div>}
                            <div className="font-medium">{p.name}</div>
                          </div>
                        </TableCell>
                        <TableCell><Badge variant="secondary">{p.category}</Badge></TableCell>
                        <TableCell className="text-right tabular-nums">{fmtIDR(p.selling_price)}</TableCell>
                        <TableCell className={`text-right tabular-nums ${low ? 'text-rose-600 font-semibold' : ''}`}>{p.stock} {low && <Badge className="ml-1 bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-50">Menipis</Badge>}</TableCell>
                        <TableCell>{p.active ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Aktif</Badge> : <Badge variant="secondary">Nonaktif</Badge>}</TableCell>
                        <TableCell className="text-right space-x-1">
                          <Button size="sm" variant="outline" onClick={() => setStockInFor(p)} data-testid={`stock-in-${p.id}`}><PackagePlus className="h-3.5 w-3.5 mr-1" />Stock In</Button>
                          {canDelete && <DeleteProductButton product={p} onDeleted={load} />}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      <StockInDialog product={stockInFor} suppliers={suppliers} onClose={() => setStockInFor(null)} onSaved={() => { load(); setStockInFor(null); }} />
    </div>
  );
}

function AddProductDialog({ suppliers, onSaved }) {
  const [sku, setSku] = useState('');
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [photoUrl, setPhotoUrl] = useState('');
  const [price, setPrice] = useState('');
  const [threshold, setThreshold] = useState(5);
  const [initialStock, setInitialStock] = useState('');
  const [supplierId, setSupplierId] = useState('');

  const submit = async () => {
    try {
      const stock = Number(initialStock) || 0;
      const body = {
        sku, name, category, photo_url: photoUrl || null,
        selling_price: Number(price), low_stock_threshold: Number(threshold) || 5,
        initial_stock: stock,
        supplier_id: stock > 0 ? supplierId : null,
      };
      if (stock > 0 && !supplierId) return toast.error('Pilih supplier untuk stok awal');
      await api.post('/products', body);
      toast.success('Barang ditambahkan');
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Gagal simpan'); }
  };

  return (
    <DialogContent className="sm:max-w-lg">
      <DialogHeader>
        <DialogTitle>Tambah Barang Baru</DialogTitle>
        <div className="text-xs text-muted-foreground">Barang langsung <b>AKTIF</b> dan bisa dijual walau HPP belum diisi Owner.</div>
      </DialogHeader>
      <div className="space-y-3 py-2 max-h-[70vh] overflow-y-auto">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label>SKU</Label><Input value={sku} onChange={(e) => setSku(e.target.value)} data-testid="product-sku" /></div>
          <div className="space-y-1.5"><Label>Kategori</Label><Input value={category} onChange={(e) => setCategory(e.target.value)} data-testid="product-category" /></div>
        </div>
        <div className="space-y-1.5"><Label>Nama Barang</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="product-name" /></div>
        <div className="space-y-1.5"><Label>URL Foto (opsional)</Label><Input value={photoUrl} onChange={(e) => setPhotoUrl(e.target.value)} placeholder="https://..." data-testid="product-photo" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label>Harga Jual (Rp)</Label><Input type="number" min="0" step="1000" value={price} onChange={(e) => setPrice(e.target.value)} data-testid="product-price" /></div>
          <div className="space-y-1.5"><Label>Low Stock Threshold</Label><Input type="number" min="0" value={threshold} onChange={(e) => setThreshold(e.target.value)} /></div>
        </div>

        <div className="rounded-lg border p-3 bg-muted/30 space-y-3">
          <div className="text-sm font-medium">Barang Masuk (Stok Awal)</div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Jumlah</Label><Input type="number" min="0" value={initialStock} onChange={(e) => setInitialStock(e.target.value)} data-testid="product-initial-stock" /></div>
            <div className="space-y-1.5">
              <Label>Supplier</Label>
              <Select value={supplierId} onValueChange={setSupplierId}>
                <SelectTrigger data-testid="product-supplier"><SelectValue placeholder="Pilih supplier" /></SelectTrigger>
                <SelectContent>
                  {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} • {s.location}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>
      <DialogFooter>
        <Button onClick={submit} data-testid="product-save-button">Simpan Barang</Button>
      </DialogFooter>
    </DialogContent>
  );
}

function StockInDialog({ product, suppliers, onClose, onSaved }) {
  const [quantity, setQuantity] = useState('');
  const [supplierId, setSupplierId] = useState('');
  const [note, setNote] = useState('');

  useEffect(() => { setQuantity(''); setSupplierId(''); setNote(''); }, [product]);

  const save = async () => {
    if (!quantity || !supplierId) return toast.error('Isi jumlah dan supplier');
    try {
      await api.post('/stock/in', { product_id: product.id, supplier_id: supplierId, quantity: Number(quantity), note });
      toast.success('Stock in berhasil');
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Gagal simpan'); }
  };

  return (
    <Dialog open={!!product} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Stock In: {product?.name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="text-sm text-muted-foreground">Stok saat ini: <b>{product?.stock}</b></div>
          <div className="space-y-1.5"><Label>Jumlah masuk</Label><Input type="number" min="1" value={quantity} onChange={(e) => setQuantity(e.target.value)} data-testid="stock-in-qty" /></div>
          <div className="space-y-1.5">
            <Label>Supplier</Label>
            <Select value={supplierId} onValueChange={setSupplierId}>
              <SelectTrigger data-testid="stock-in-supplier"><SelectValue placeholder="Pilih supplier" /></SelectTrigger>
              <SelectContent>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Catatan (opsional)</Label><Input value={note} onChange={(e) => setNote(e.target.value)} /></div>
        </div>
        <DialogFooter><Button onClick={save} data-testid="stock-in-save">Simpan</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function DeleteProductButton({ product, onDeleted }) {
  const [loading, setLoading] = React.useState(false);
  const doDelete = async () => {
    setLoading(true);
    try {
      await api.delete(`/products/${product.id}`);
      toast.success(`Barang "${product.name}" dihapus`);
      onDeleted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal menghapus');
    } finally { setLoading(false); }
  };
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button size="sm" variant="outline" className="text-rose-600 hover:text-rose-700 hover:bg-rose-50 border-rose-200" data-testid={`delete-product-${product.id}`}>
          <Trash2 className="h-3.5 w-3.5 mr-1" />Hapus
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent data-testid="delete-product-confirm">
        <AlertDialogHeader>
          <AlertDialogTitle>Hapus barang "{product.name}"?</AlertDialogTitle>
          <AlertDialogDescription>
            Barang akan dihapus permanen dari sistem. Transaksi lama yang menggunakan barang ini tetap aman (nama & SKU sudah tersimpan sebagai snapshot).
            <br /><br />
            <b>SKU:</b> {product.sku} • <b>Stok saat ini:</b> {product.stock}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Batal</AlertDialogCancel>
          <AlertDialogAction onClick={doDelete} disabled={loading} className="bg-rose-600 hover:bg-rose-700" data-testid="delete-product-confirm-button">
            {loading ? 'Menghapus...' : 'Ya, Hapus'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

